import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { login, signup, verifyOtp, resetPassword, changePassword, logout } from "../../api/auth";
import { clearUser, fetchProfile, setUser } from "./userSlice";

const initialState = {
  loading: false,
  error: null,
  message: null
};


// ---------------- Async Thunks ----------------

export const signinUser = createAsyncThunk(
  "auth/signinUser",
  async (credentials, { dispatch, rejectWithValue }) => {
    try {

      const response = await login(credentials)

      dispatch(setUser(response))

      return response.data[0]

    } catch (error) {
      return rejectWithValue(error.response?.data)
    }
  }
)

// SIGNUP
export const signupUser = createAsyncThunk(
  "auth/signupUser",
  async (userData, { rejectWithValue }) => {
    try {

      const response = await signup(userData);

      return response.data;

    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Signup failed" }
      );
    }
  }
);


// VERIFY OTP
export const verifyUserOTP = createAsyncThunk(
  "auth/verifyUserOTP",
  async (payload, { dispatch, rejectWithValue }) => {
    try {

      const response = await verifyOtp(payload)

      dispatch(setUser(response))

      return response.data

    } catch (error) {
      return rejectWithValue(error.response?.data)
    }
  }
)


// RESET PASSWORD
export const resetUserPassword = createAsyncThunk(
  "auth/resetUserPassword",
  async ({ email }, { rejectWithValue }) => {
    try {

      const response = await resetPassword({ email });

      return response.data;

    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Password reset failed" }
      );
    }
  }
);


// CHANGE PASSWORD
export const changeUserPassword = createAsyncThunk(
  "auth/changeUserPassword",
  async (passwordData, { rejectWithValue }) => {
    try {

      const response = await changePassword(passwordData);

      return response.data;

    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Change password failed" }
      );
    }
  }
);


// LOGOUT
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { dispatch, rejectWithValue }) => {
    try {

      const response = await logout();

      dispatch(clearUser());

      return response.data;

    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Logout failed" }
      );
    }
  }
);


// ---------------- Slice ----------------

export const authSlice = createSlice({
  name: "auth",
  initialState,

  reducers: {

    clearAuthError: (state) => {
      state.error = null;
    },

    clearAuthMessage: (state) => {
      state.message = null;
    }

  },

  extraReducers: (builder) => {

    builder

      // LOGIN
      .addCase(signinUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(signinUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "Signin successful";
      })

      .addCase(signinUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Signin failed";
      })


      // SIGNUP
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "Signup successful. Verify OTP";
      })

      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Signup failed";
      })


      // VERIFY OTP
      .addCase(verifyUserOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(verifyUserOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "OTP verified";
      })

      .addCase(verifyUserOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "OTP verification failed";
      })


      // RESET PASSWORD
      .addCase(resetUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(resetUserPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "Password reset successful";
      })

      .addCase(resetUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Password reset failed";
      })


      // CHANGE PASSWORD
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(changeUserPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "Password changed successfully";
      })

      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Change password failed";
      })


      // LOGOUT
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.message = null;
      });

  }
});

export const { clearAuthError, clearAuthMessage } = authSlice.actions;
export default authSlice.reducer;